import { useTheme } from '../theme/ThemeContext';
import { getModalTheme } from '../theme/themeStyles';

export type FieldProgressVariant = 'split' | 'split-saving' | 'delineation';

type FieldProgressOverlayProps = {
  variant: FieldProgressVariant | null;
};

const COPY: Record<
  FieldProgressVariant,
  { title: string; subtitle: string; labelledBy: string; describedBy: string }
> = {
  split: {
    title: 'Splitting field',
    subtitle: 'Detecting boundaries and separating into subplots…',
    labelledBy: 'field-progress-title',
    describedBy: 'field-progress-desc',
  },
  'split-saving': {
    title: 'Saving split blocks',
    subtitle: 'Writing the new blocks to your Digisaka account…',
    labelledBy: 'field-progress-title',
    describedBy: 'field-progress-desc',
  },
  delineation: {
    title: 'AI field delineation',
    subtitle: 'Scanning the map view and tracing field boundaries…',
    labelledBy: 'field-progress-title',
    describedBy: 'field-progress-desc',
  },
};

function SplitVisual({ isDark }: { isDark: boolean }) {
  const line = isDark ? '#4ade80' : '#2f6b4f';
  const accent = isDark ? '#34d399' : '#c98a3e';
  const fills = isDark
    ? {
        tl: 'rgba(74, 222, 128, 0.28)',
        tr: 'rgba(74, 222, 128, 0.22)',
        bl: 'rgba(74, 222, 128, 0.18)',
        br: 'rgba(74, 222, 128, 0.14)',
      }
    : {
        tl: '#d8e6da',
        tr: '#cde2d1',
        bl: '#c6dac9',
        br: '#bcd4c1',
      };

  return (
    <div className="farm-split-4way-stage mx-auto flex h-[168px] w-full items-center justify-center">
      <svg viewBox="0 0 240 220" className="h-[172px] w-[188px] overflow-visible" aria-hidden>
        <g className="farm-split-quad farm-split-quad--tl">
          <path
            d="M35,115 L60,40 L115,34 Q116,73 118,112 Q76,113 35,115 Z"
            fill={fills.tl}
            stroke={line}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </g>
        <g className="farm-split-quad farm-split-quad--tr">
          <path
            d="M115,34 L170,30 L200,110 Q159,111 118,112 Q116,73 115,34 Z"
            fill={fills.tr}
            stroke={line}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </g>
        <g className="farm-split-quad farm-split-quad--br">
          <path
            d="M118,112 Q159,111 200,110 L175,190 L122,192 Q120,152 118,112 Z"
            fill={fills.br}
            stroke={line}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </g>
        <g className="farm-split-quad farm-split-quad--bl">
          <path
            d="M122,192 L70,195 L35,115 Q76,113 118,112 Q120,152 122,192 Z"
            fill={fills.bl}
            stroke={line}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </g>

        <path
          className="farm-split-divider farm-split-divider--v"
          d="M115,34 Q116,73 118,112 Q120,152 122,192"
          fill="none"
          stroke={accent}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray="8 7"
        />
        <path
          className="farm-split-divider farm-split-divider--h"
          d="M35,115 Q76,113 118,112 Q159,111 200,110"
          fill="none"
          stroke={accent}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray="8 7"
        />

        {/* Outer corner brackets */}
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0s' }}>
          <path
            transform="translate(60,40)"
            d="M -8,4 L -8,-8 L 4,-8"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0.06s' }}>
          <path
            transform="translate(170,30)"
            d="M -4,-8 L 8,-8 L 8,4"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0.12s' }}>
          <path
            transform="translate(200,110)"
            d="M 8,-6 L 8,8 L -4,8"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0.18s' }}>
          <path
            transform="translate(175,190)"
            d="M 6,-8 L 8,6 L -6,8"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0.24s' }}>
          <path
            transform="translate(70,195)"
            d="M 6,8 L -8,8 L -8,-6"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket" style={{ ['--d' as string]: '0.3s' }}>
          <path
            transform="translate(35,115)"
            d="M -8,-6 L -8,8 L 4,8"
            stroke={line}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>

        {/* New split points */}
        <g className="farm-split-bracket farm-split-bracket--new" style={{ ['--d' as string]: '0.2s' }}>
          <path
            transform="translate(115,34)"
            d="M -9,7 L -9,-9 L 7,-9 M -9,7 L 9,7 L 9,-9"
            stroke={accent}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket farm-split-bracket--new" style={{ ['--d' as string]: '0.2s' }}>
          <path
            transform="translate(122,192)"
            d="M -9,-7 L -9,9 L 7,9 M -9,-7 L 9,-7 L 9,9"
            stroke={accent}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className="farm-split-bracket farm-split-bracket--center" style={{ ['--d' as string]: '0.26s' }}>
          <path
            transform="translate(118,112)"
            d="M 0,-9 L 0,9 M -9,0 L 9,0"
            stroke={accent}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </div>
  );
}

/** Candidate → confirmed field outlines with scan sweep (ai-field-delineation reference). */
function DelineationVisual({ isDark }: { isDark: boolean }) {
  const line = isDark ? '#4ade80' : '#2f6b4f';
  const candidate = isDark ? 'rgba(185, 199, 187, 0.55)' : '#b9c7bb';
  const confirmedFill = isDark ? 'rgba(74, 222, 128, 0.28)' : '#d8e6da';
  const accent = isDark ? '#34d399' : '#c98a3e';
  const frameBg = isDark ? 'rgba(255,255,255,0.04)' : '#eef1ea';
  const frameBorder = isDark ? 'rgba(255,255,255,0.1)' : '#dde3d8';

  return (
    <div className="ai-delineate-stage mx-auto mb-3.5 flex h-[150px] w-full items-center justify-center">
      <div
        className="ai-delineate-frame relative h-[150px] w-[220px] overflow-hidden rounded-2xl border"
        style={{ background: frameBg, borderColor: frameBorder }}
      >
        <svg viewBox="0 0 220 150" className="block h-full w-full overflow-visible" aria-hidden>
          <g className="ai-delineate-shape ai-delineate-shape--a">
            <polygon
              className="ai-delineate-candidate"
              points="-34,-30 33,-37 37,32 -37,29"
              transform="translate(70,55) rotate(-8)"
              fill="none"
              stroke={candidate}
              strokeWidth="2"
              strokeDasharray="5 4"
            />
            <polygon
              className="ai-delineate-confirmed"
              points="-34,-30 33,-37 37,32 -37,29"
              transform="translate(70,55) rotate(-8)"
              fill={confirmedFill}
              stroke={line}
              strokeWidth="2.4"
            />
          </g>

          <g className="ai-delineate-shape ai-delineate-shape--b">
            <polygon
              className="ai-delineate-candidate"
              points="-32,-28 33,-35 35,31 -30,29"
              transform="translate(148,52) rotate(5)"
              fill="none"
              stroke={candidate}
              strokeWidth="2"
              strokeDasharray="5 4"
            />
            <polygon
              className="ai-delineate-confirmed"
              points="-32,-28 33,-35 35,31 -30,29"
              transform="translate(148,52) rotate(5)"
              fill={confirmedFill}
              stroke={line}
              strokeWidth="2.4"
            />
          </g>

          <g className="ai-delineate-shape ai-delineate-shape--c">
            <polygon
              className="ai-delineate-candidate"
              points="-25,-21 26,-25 27,23 -23,21"
              transform="translate(85,113) rotate(-16)"
              fill="none"
              stroke={candidate}
              strokeWidth="2"
              strokeDasharray="5 4"
            />
            <polygon
              className="ai-delineate-confirmed"
              points="-25,-21 26,-25 27,23 -23,21"
              transform="translate(85,113) rotate(-16)"
              fill={confirmedFill}
              stroke={line}
              strokeWidth="2.4"
            />
          </g>

          <g className="ai-delineate-scanner">
            <rect x="-10" y="-7" width="240" height="14" fill={accent} opacity="0.16" />
            <line
              x1="-10"
              y1="0"
              x2="230"
              y2="0"
              stroke={accent}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

/**
 * Full-screen blur overlay while Split or AI field delineation runs.
 */
export default function FieldProgressOverlay({ variant }: FieldProgressOverlayProps) {
  const { isDark } = useTheme();
  const md = getModalTheme(isDark);

  if (!variant) return null;

  const copy = COPY[variant];
  const isDelineation = variant === 'delineation';

  return (
    <div
      className={`fixed inset-0 z-[9990] flex items-center justify-center p-4 ${md.overlay} backdrop-blur-md`}
      role="alertdialog"
      aria-busy="true"
      aria-live="polite"
      aria-labelledby={copy.labelledBy}
      aria-describedby={copy.describedBy}
    >
      <div
        className={`farm-split-progress-card w-full max-w-[340px] rounded-[22px] border px-[30px] pb-7 pt-[34px] text-center shadow-xl ${
          isDark
            ? 'border-[#2d3748] bg-[#1c2432]/95 text-[#e6edf3]'
            : 'border-transparent bg-[#f7f8f3] text-[#1f2b22]'
        }`}
      >
        {isDelineation ? (
          <DelineationVisual isDark={isDark} />
        ) : (
          <SplitVisual isDark={isDark} />
        )}

        <h2 id={copy.labelledBy} className="mt-[18px] text-[19px] font-semibold tracking-tight">
          {copy.title}
        </h2>
        <p
          id={copy.describedBy}
          className={`mt-1.5 mb-5 text-[14.5px] leading-snug ${
            isDark ? 'text-[#a8a8a3]' : 'text-[#5c6b5e]'
          }`}
        >
          {copy.subtitle}
        </p>

        <div
          className={`relative mx-auto h-[5px] w-full overflow-hidden rounded-[3px] ${
            isDark ? 'bg-white/[0.08]' : 'bg-[#e5e6dc]'
          }`}
          aria-hidden
        >
          <div
            className={`farm-split-progress-bar absolute inset-y-0 w-[38%] rounded-[3px] ${
              isDark ? 'bg-[#4ade80]' : 'bg-[#2f6b4f]'
            }`}
          />
        </div>
      </div>
    </div>
  );
}

/** @deprecated Use FieldProgressOverlay — kept as alias for existing imports. */
export { FieldProgressOverlay as FarmSplitProgressOverlay };
